import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/models/api-response.model';
import { PageResponseTimes, TimeResumo } from 'src/app/pages/empresa/pages/times/models/time.model';
import { unwrapConteudo } from '../utils/api.utils';

@Injectable({
  providedIn: 'root'
})
export class TimesApiService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  listar(
    pagina: number,
    tamanho: number,
    filtro?: string,
    departamentoId?: number | null
  ): Observable<PageResponseTimes<TimeResumo>> {
    let params = new HttpParams()
      .set('page', pagina)
      .set('size', tamanho);

    if (filtro?.trim()) {
      params = params.set('filtro', filtro.trim());
    }

    if (departamentoId !== null && departamentoId !== undefined) {
      params = params.set('departamentoId', departamentoId);
    }

    return this.http
      .get<ApiResponse<PaginaTimesRespostaApi>>(`${this.baseUrl}/api/v1/times`, { params })
      .pipe(
        map((res) => {
          const page = unwrapConteudo(res);
          const mapped: PageResponseTimes<TimeResumo> = {
            content: (page.conteudo ?? []) as TimeResumo[],
            totalElements: page.totalElementos ?? 0,
            totalPages: page.totalPaginas ?? 0,
            size: page.tamanhoPagina ?? tamanho,
            number: page.pagina ?? pagina
          };
          return this.normalizePage(mapped, tamanho);
        })
      );
  }

  criar(payload: { nome: string; departamentoId: number; descricao?: string | null }): Observable<TimeResumo> {
    return this.http
      .post<ApiResponse<TimeResumo>>(`${this.baseUrl}/api/v1/times`, payload)
      .pipe(map((res) => unwrapConteudo(res)));
  }

  atualizar(id: number, payload: { nome: string; departamentoId: number; descricao?: string | null }): Observable<TimeResumo> {
    return this.http
      .put<ApiResponse<TimeResumo>>(`${this.baseUrl}/api/v1/times/${id}`, payload)
      .pipe(map((res) => unwrapConteudo(res)));
  }

  excluir(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/v1/times/${id}`);
  }

  listarComHierarquia(): Observable<TimeResumo[]> {
    return this.listar(0, 1000).pipe(map((page) => page.content));
  }

  listarMeus(): Observable<TimeResumo[]> {
    return this.http
      .get<ApiResponse<MeusTimesRespostaApi[]>>(`${this.baseUrl}/api/v1/me/times`)
      .pipe(
        map((res) => {
          const itens = (unwrapConteudo(res) as unknown as MeusTimesRespostaApi[]) ?? [];
          return itens.map((t) => ({
            id: t.id,
            nome: t.nome,
            descricao: null,
            departamentoId: 0,
            departamentoNome: t.departamentoNome ?? null,
            quantidadeColaboradores: t.totalColaboradores ?? 0,
            gestores: []
          }) as TimeResumo);
        })
      );
  }

  private normalizePage(response: PageResponseTimes<TimeResumo>, fallbackSize: number): PageResponseTimes<TimeResumo> {
    return {
      ...response,
      size: response.size ?? fallbackSize,
      content: (response.content ?? []).map((t) => ({
        ...t,
        descricao: t.descricao ?? null,
        departamentoNome: t.departamentoNome ?? null,
        gestores: t.gestores ?? [],
        quantidadeColaboradores: t.quantidadeColaboradores ?? 0
      }))
    };
  }

}

interface PaginaTimesRespostaApi {
  conteudo: TimeResumo[];
  pagina: number;
  tamanhoPagina: number;
  totalElementos: number;
  totalPaginas: number;
  primeira: boolean;
  ultima: boolean;
}

/** Shape do item retornado por GET /api/v1/me/times (MeuTimeItemResponseDto). */
interface MeusTimesRespostaApi {
  id: number;
  nome: string;
  departamentoNome: string | null;
  totalColaboradores: number;
}

